import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure, ApiResult } from '@/api/client'
import { useAuth } from '@/auth/context'
import { followHandoff } from '@/auth/handoff'
import { useBrand } from '@/brand/context'
import { TenantContext } from './context'
import type { TenantMembership, TenantValue } from './context'

/**
 * Which organisations this user belongs to, and which one the session is in.
 *
 * The two halves come from different places on purpose. The **list** is
 * `GET /api/v1/tenants`, scoped by the caller's own user id. The **active one**
 * is `session.tenant_id`, because the session is the authority — a request that
 * names any other tenant is refused with `TENANT_MISMATCH` no matter what this
 * SPA believes (`api/app/tenancy/dependencies.py`). Keeping a separate
 * client-side "current organisation" would be a second answer to a question
 * that already has one, and the two would eventually disagree.
 *
 * So `select()` does not update anything here at all. Since 2026-09-13 each
 * organisation lives on its own host and a session is bound to one (spec:
 * *Sessions and the handoff*), so switching organisation is *leaving*: `POST
 * /auth/handoff` mints a token for the other host and the browser goes there,
 * where `Handoff` turns it into that host's session. There is no state to
 * update on this side of the move, and a UI that showed the new organisation
 * on the old host would 401 on the very next request.
 *
 * **`adopt()` exists because a refetch is not instant.** Creating an
 * organisation and accepting an invitation both return the tenant *and* select
 * it on the session, so at that moment the SPA knows both halves — but a
 * `reload()` only *starts* a request. Navigating into the shell before it
 * lands means `RequireTenant` sees a session pointing at an organisation that
 * is not in the list yet and sends the user straight back to the picker. That
 * is not hypothetical: it is what the first live run of `e2e/shell.spec.ts`
 * did, on a signup that had otherwise worked perfectly.
 *
 * Written with `useState` rather than `useQuery` deliberately. This is the one
 * read in the app that is auth-dependent, adoptable, and must survive a
 * refetch without emptying — three exceptions to what that hook is for, and
 * bending it to fit would make it worse for the twenty ordinary reads.
 */
export function TenantProvider({ children }: { children: ReactNode }): JSX.Element {
  const { status, session, refresh } = useAuth()
  const brand = useBrand()
  const tenantHost = brand.host === 'tenant'
  const activeId = session?.tenant_id ?? null
  // The identity of the session itself, not of the organisation on it. See the
  // `attempted` note below: a re-authentication replaces this while `status`
  // stays `authenticated` throughout, and that is the difference the landing
  // guard has to be able to see.
  const sessionId = session?.id ?? null
  const authenticated = status === 'authenticated'

  const [memberships, setMemberships] = useState<TenantMembership[] | null>(null)
  // The organisation sign-in lands in, as the *server* resolved it — the stored
  // default where there is one, and the oldest membership where there is not.
  // Held beside the list rather than derived from it because the fallback rule
  // is `api/app/tenancy/default_org.py`'s, and a second copy of it here is a
  // second thing to keep in step (REQUIREMENTS §9.1).
  const [defaultTenantId, setDefaultTenantId] = useState<string | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [attempt, setAttempt] = useState(0)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!authenticated) {
      // No request worth making, and the previous user's organisations are
      // discarded so the next person to sign in on this browser never sees them.
      setMemberships(null)
      setDefaultTenantId(null)
      setFailure(null)
      return
    }

    const controller = new AbortController()
    void (async () => {
      try {
        const result = await call(() => api.GET('/api/v1/tenants', { signal: controller.signal }))
        if (controller.signal.aborted || !mounted.current) return
        if (result.ok) {
          setMemberships(result.data.tenants)
          setDefaultTenantId(result.data.default_tenant_id ?? null)
          setFailure(null)
        } else {
          setFailure(result.failure)
        }
      } catch {
        // `call()` re-throws only AbortError, which is this effect cleaning up.
      }
    })()

    return () => {
      controller.abort()
    }
  }, [authenticated, attempt])

  const reload = useCallback(() => {
    setAttempt((n) => n + 1)
  }, [])

  const adopt = useCallback((row: TenantMembership) => {
    setMemberships((current) =>
      // Ordered by name, matching what `GET /api/v1/tenants` returns, so the
      // switcher does not reshuffle when the real list arrives a moment later.
      [...(current ?? []).filter((existing) => existing.tenant.id !== row.tenant.id), row].sort(
        (a, b) => a.tenant.name.localeCompare(b.tenant.name),
      ),
    )
    setFailure(null)
  }, [])

  const select = useCallback(
    async (tenantId: string, next?: string): Promise<ApiResult<void>> => {
      // Already on this organisation's host: nothing to cross, and a handoff
      // to the host one is standing on would spend a token to arrive where one
      // already is. Only on a tenant host — on the apex the session may name
      // an organisation (`select-default` below) and the shell is still
      // unreachable there, so the move is real.
      if (tenantHost && tenantId === activeId) return { ok: true, data: undefined }
      const result = await call(() =>
        api.POST('/api/v1/auth/handoff/{tenant_id}', {
          params: { path: { tenant_id: tenantId } },
        }),
      )
      // The failure is handed back whole rather than flattened to `false`: only
      // the caller knows which of these is worth a sentence of its own (WEB-9).
      if (!result.ok) return result
      followHandoff(result.data.url, next)
      return { ok: true, data: undefined }
    },
    [tenantHost, activeId],
  )

  /**
   * Setting where sign-in lands, from the switcher's *Set as default*.
   *
   * The server's answer is what updates the badge, not the id that was asked
   * for. Two clicks on a slow connection can settle in the other order, and an
   * optimistic update would leave the tick on the first organisation while the
   * user row held the second.
   */
  const setDefault = useCallback(async (tenantId: string): Promise<ApiResult<void>> => {
    const result = await call(() =>
      api.PUT('/api/v1/tenants/{tenant_id}/default', {
        params: { path: { tenant_id: tenantId } },
      }),
    )
    if (!result.ok) return result
    setDefaultTenantId(result.data.default_tenant_id)
    return { ok: true, data: undefined }
  }, [])

  /**
   * Landing a fresh session, which is what stopped the picker being a toll gate.
   *
   * Signing in creates a **new** session, and a new session has no organisation
   * on it — `POST /auth/login` has no way to know which one to pick. This used
   * to be answered here for exactly one shape of account: somebody belonging to
   * a single organisation had it selected for them, and everybody else was sent
   * to `/organisations`. So an agency owner with four clients paid a full-page
   * interstitial on **every sign-in** to answer a question whose answer had not
   * changed since the day they signed up (the owner's decision, 2026-09-10).
   *
   * `POST /api/v1/tenants/select-default` is now the whole of it. The rule it
   * applies — the default they set, else their oldest membership, else nothing —
   * lives in `api/app/tenancy/default_org.py` and is deliberately *not*
   * reimplemented here: this component would have to re-derive "oldest" from a
   * list ordered by name, and would be choosing from a list fetched a moment
   * ago rather than from the memberships as they are at the instant of the
   * switch.
   *
   * **It does not wait for the membership list**, and that is the point of
   * having the server resolve it. The old code could not start until `GET
   * /tenants` had landed, because it needed the list to count. This fires the
   * moment the session is known, in parallel with that read, so the shell is
   * reached in one round trip rather than two chained ones.
   *
   * `attempted` is what stops a refusal from looping. An account with no
   * organisations answers `409 TENANT_NOT_SELECTED` — a real state, not a fault
   * — and without the ref this effect would re-run on every render forever.
   *
   * **It holds the session id it was spent on, not a bare `true`, and that is a
   * fix rather than a flourish.** A boolean is scoped to this component, and
   * this component never unmounts: `App.tsx` mounts it above the router. So it
   * had to be cleared by hand when `authenticated` went false — and a **session
   * expiry never makes `authenticated` false**. `AuthProvider` deliberately
   * keeps the identity and raises `expired`, `SessionExpired` re-authenticates
   * with `POST /auth/login`, and that mints a session with `tenant_id = null`
   * like every other login. The flag was still spent from the *previous*
   * session, so the effect returned early, `landingPending` was false, and
   * `RequireTenant` sent the user to the picker — the exact bug this whole
   * change exists to remove, surviving on the one path nobody thought to look
   * at. Keying to `session.id` makes a replaced session a new question, which
   * is what it is; the no-loop guarantee is untouched because the id is stable
   * for that session's whole life.
   */
  const [landing, setLanding] = useState(false)
  const attempted = useRef<string | null>(null)

  useEffect(() => {
    if (!authenticated || sessionId === null) {
      attempted.current = null
      return
    }
    // On an organisation's own host the session was minted bound to it —
    // login binds, the handoff binds — so there is nothing to resolve, and a
    // landing call would be asking the server to move a session it refuses to
    // move (spec: *Sessions and the handoff*). A tenant-host session with no
    // organisation on it is a state the API does not produce; `RequireTenant`
    // sends it to the picker.
    if (tenantHost) return
    if (activeId !== null || attempted.current === sessionId) return
    attempted.current = sessionId
    setLanding(true)
    void (async () => {
      const result = await call(() => api.POST('/api/v1/tenants/select-default'))
      // The failure is swallowed rather than surfaced, and only this one is.
      // Every reason this can fail ends in the same place — `RequireTenant`
      // sends the user to the picker, where the organisations are listed with
      // their own error handling and a *New organisation* button. An error
      // banner here would be a second report of a state the next screen already
      // explains, and for the ordinary no-organisations account it would be an
      // error message about not having done something yet.
      if (result.ok) await refresh()
      if (mounted.current) setLanding(false)
    })()
  }, [authenticated, sessionId, activeId, refresh, tenantHost])

  // Read during render, not after it. The effect above runs *after* the commit,
  // so without this there is one render reporting `ready` with no active
  // organisation — and `RequireTenant` redirects to the picker on it, which is
  // the exact frame the landing exists to avoid. Once the attempt has been made
  // and has settled, this goes false and the redirect happens for the accounts
  // that genuinely have nowhere to go.
  const landingPending =
    authenticated &&
    !tenantHost &&
    activeId === null &&
    (attempted.current !== sessionId || landing)

  const value = useMemo<TenantValue>(() => {
    const rows = memberships ?? []
    return {
      // `ready` for an anonymous visitor: there is nothing to wait for, and no
      // shell will ask — `RequireTenant` sits behind `RequireAuth`. A failure
      // is only `error` while there is no list to fall back on; a *reload* that
      // fails leaves the shell standing on what it already had.
      status: !authenticated
        ? 'ready'
        : landingPending
          ? 'loading'
          : memberships !== null
            ? 'ready'
            : failure
              ? 'error'
              : 'loading',
      failure,
      memberships: rows,
      // Matched against the list rather than trusted from the session alone:
      // the session may point at an organisation this user has since been
      // removed from, and rendering a shell for one they cannot read would
      // fail on the first request instead of at the boundary.
      active: rows.find((row) => row.tenant.id === activeId) ?? null,
      // Matched against the list for the same reason `active` is: the column can
      // name an organisation the user has since been removed from, and the
      // switcher must not put a *Default* tick on a row that is not there. The
      // server clears such a value on the next landing; until then this is what
      // stops the badge outliving the membership.
      defaultTenantId: rows.find((row) => row.tenant.id === defaultTenantId)?.tenant.id ?? null,
      select,
      setDefault,
      adopt,
      reload,
    }
  }, [
    memberships,
    defaultTenantId,
    failure,
    landingPending,
    authenticated,
    activeId,
    select,
    setDefault,
    adopt,
    reload,
  ])

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}
